import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import ZebraPlayer from './zebra-player';
import AppContext from '../utils/app-context';

const HOVER_DELAY_MS = 500;
const PREVIEW_WIDTH = 320;

/**
 * RefHoverPreview
 *
 * Wraps a reference thumbnail and shows a floating enlarged preview (video or
 * image) above-right of the thumbnail after a 500 ms hover delay.
 *
 * Uses a React Portal to render the preview at the document root, so it is
 * never clipped by ancestor overflow:hidden / scroll containers.
 *
 * Props:
 *  - videoUrl   {string|null}  URL of the .mp4 video to preview (if ref is a video)
 *  - imageUrl   {string|null}  URL of the image to preview (if ref is an image)
 *  - children   ReactNode      The original thumbnail content
 *  - ...rest    any             Forwarded to the outer wrapper Box (sx, onClick, etc.)
 */
export default function RefHoverPreview({ videoUrl, imageUrl, children, sx, ...rest }) {
  const { state } = useContext(AppContext);
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const anchorRef = useRef(null);

  const isVertical = state.stories.activeStory?.is_vertical;

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    // Position above-right of the thumbnail
    setPosition({
      top: rect.top - 8,  // 8px gap above
      left: rect.left + rect.width / 2,
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!videoUrl && !imageUrl) return;
    timerRef.current = setTimeout(() => {
      updatePosition();
      setShow(true);
    }, HOVER_DELAY_MS);
  }, [videoUrl, imageUrl, updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShow(false);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Determine preview dimensions respecting aspect ratio
  const previewWidth = isVertical ? 'auto' : `${PREVIEW_WIDTH}px`;
  const previewHeight = isVertical ? `${PREVIEW_WIDTH}px` : 'auto';

  const playerStyles = {
    maxWidth: '100%',
    maxHeight: '100%',
    margin: '0 auto',
  };

  const floatingPreview = show && (videoUrl || imageUrl) ? createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateY(-100%)',
        zIndex: 1500,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        background: 'var(--bg-primary, #1a1a1a)',
        width: previewWidth,
        height: previewHeight,
        pointerEvents: 'none',
      }}
    >
      {videoUrl ? (
        <ZebraPlayer
          video_urls={[videoUrl]}
          width={isVertical ? null : '100%'}
          height={isVertical ? '100%' : null}
          playerStyles={playerStyles}
          playing={true}
          loop
          muted
          controls={false}
        />
      ) : (
        <Box
          component="img"
          src={imageUrl}
          alt="reference preview"
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </Box>,
    document.body
  ) : null;

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: 'relative', ...sx }}
      {...rest}
    >
      {children}
      {floatingPreview}
    </Box>
  );
}
